import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  LuEllipsis,
  LuEye,
  LuPencil,
  LuCircleDollarSign,
  LuTruck,
  LuTrash2,
} from "react-icons/lu";
import InvoicePDFButton from "./pdf/InvoicePDFButton";
import { canEditPayment } from "./salesMetrics";
import "./InvoiceActionsMenu.css";

const MENU_WIDTH = 200;
const GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Posição do menu: abaixo do botão, alinhado à direita; se não couber
 * embaixo (últimas linhas da tabela), abre pra cima.
 */
export function placeMenu(buttonRect, menuHeight, viewportHeight) {
  const left = Math.max(VIEWPORT_MARGIN, buttonRect.right - MENU_WIDTH);
  const below = buttonRect.bottom + GAP;
  const fitsBelow = below + menuHeight <= viewportHeight - VIEWPORT_MARGIN;
  const above = buttonRect.top - GAP - menuHeight;
  const top =
    fitsBelow || above < VIEWPORT_MARGIN
      ? Math.min(
          below,
          Math.max(
            VIEWPORT_MARGIN,
            viewportHeight - VIEWPORT_MARGIN - menuHeight,
          ),
        )
      : above;
  return { top, left };
}

/**
 * Menu "⋯" de ações de um pedido na tabela de Vendas. Renderiza num portal
 * com posição fixa pra não ser cortado pelo overflow da react-table.
 */
const InvoiceActionsMenu = ({
  order,
  onView,
  onEdit,
  onEditOrderManually,
  onDelete,
  onPayment,
}) => {
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const open = Boolean(position);

  const close = () => setPosition(null);

  const toggle = () => {
    if (open) return close();
    // Primeiro abre abaixo; o layout effect mede a altura real e reposiciona
    // (pra cima, se precisar) antes do navegador pintar.
    const rect = buttonRef.current.getBoundingClientRect();
    setPosition(placeMenu(rect, 0, window.innerHeight));
  };

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const next = placeMenu(
      buttonRef.current.getBoundingClientRect(),
      menuRef.current.offsetHeight,
      window.innerHeight,
    );
    if (next.top !== position.top || next.left !== position.left) {
      setPosition(next);
    }
  }, [open, position]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (
        !menuRef.current?.contains(e.target) &&
        !buttonRef.current?.contains(e.target)
      )
        close();
    };
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const run = (fn) => () => {
    close();
    fn(order);
  };

  const items = [
    { label: "Ver pedido", title: "Ver", icon: LuEye, fn: onView },
    order.description && {
      label: "Editar pedido",
      title: "Editar Pedido",
      icon: LuPencil,
      fn: onEditOrderManually,
    },
    onPayment &&
      canEditPayment(order) && {
        label: "Financeiro",
        title: "Financeiro",
        icon: LuCircleDollarSign,
        fn: onPayment,
      },
    {
      label: "Editar entrega",
      title: "Editar Entrega",
      icon: LuTruck,
      fn: onEdit,
    },
  ].filter(Boolean);

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        className="invoice-actions__trigger"
        aria-label="Ações"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <LuEllipsis size={18} />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="invoice-actions__menu"
            style={{
              top: position.top,
              left: position.left,
              width: MENU_WIDTH,
            }}
          >
            {items.map(({ label, title, icon: Icon, fn }) => (
              <button
                key={label}
                type="button"
                role="menuitem"
                title={title}
                className="invoice-actions__item"
                onClick={run(fn)}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
            <InvoicePDFButton
              order={order}
              className="invoice-actions__item"
              label="Baixar PDF"
            />
            <div className="invoice-actions__divider" />
            <button
              type="button"
              role="menuitem"
              title="Excluir"
              className="invoice-actions__item invoice-actions__item--danger"
              onClick={run(onDelete)}
            >
              <LuTrash2 size={15} />
              Excluir
            </button>
          </div>,
          document.body,
        )}
    </>
  );
};

export default InvoiceActionsMenu;
